import { useCallback, useEffect, useLayoutEffect, type RefObject } from "react";

export function useAutosizeTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
): void {
  const resize = useCallback(() => {
    const ta = ref.current;
    if (!ta) return;
    const cs = getComputedStyle(ta);
    const line = parseFloat(cs.lineHeight);
    const padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const max = Number.isFinite(line) ? Math.round(line * COMPOSER_MAX_LINES + padding) : null;
    if (max !== null) ta.style.maxHeight = `${max}px`;
    if (ta.value.length === 0 && Number.isFinite(line)) {
      ta.style.height = `${Math.round(line + padding)}px`;
      return;
    }
    ta.style.height = "auto";
    ta.style.height = max === null ? `${ta.scrollHeight}px` : `${Math.min(ta.scrollHeight, max)}px`;
  }, [ref]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: controlled value changes must remeasure the textarea even though resize reads the DOM value.
  useLayoutEffect(resize, [resize, value]);

  useEffect(() => {
    const ta = ref.current;
    if (!ta || typeof ResizeObserver === "undefined") return;
    let lastWidth = -1;
    const ro = new ResizeObserver(() => {
      if (ta.clientWidth === lastWidth) return;
      lastWidth = ta.clientWidth;
      resize();
    });
    ro.observe(ta);
    return () => ro.disconnect();
  }, [ref, resize]);
}

const COMPOSER_MAX_LINES = 5;
